<template>
  <div class="modal-consulte-bg" :key="refreshCounter" />
  <div
    ref="dialogRef"
    class="modal fade show"
    tabindex="-1"
    role="dialog"
    :key="refreshCounter"
    :aria-labelledby="idTitulo"
    aria-modal="true"
    style="display: unset; border-radius: var(--ct-modal-inner-border-radius)"
    @keydown.esc="close"
    @keydown="manterFocoDentro"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <slot name="header">
            <!-- O `aria-labelledby` do diálogo aponta para ESTE id. Antes era o literal
                 "standard-modalLabel", que não existe em nenhuma tela: a referência
                 quebrada deixava o modal sem nome acessível. -->
            <h4 class="modal-title" :id="idTitulo">{{ titulo }}</h4>
          </slot>
          <button type="button" class="btn-close" @click="close" aria-label="Fechar"></button>
        </div>
        <div class="modal-body">
          <!--
            ESCOLA e SITUAÇÃO, os dois recortes que toda pesquisa de estudante oferece.

            Eles são complementares de propósito: a escola diz ONDE procurar e a situação diz
            QUEM — é da combinação que saem as perguntas do atendimento ("não matriculados nesta
            escola", "não matriculados na rede"). Ficam aqui, no componente compartilhado, e não
            em cada tela: são catorze telas usando esta pesquisa, e uma cópia em cada uma seria
            catorze comportamentos diferentes na primeira correção.

            Só aparecem quando a tela NÃO impôs o próprio recorte (`somenteEnturmadosMatriculados`
            ou `somenteMatriculadosNaEscola`): ali o universo já é uma decisão da tela — a
            rematrícula precisa de quem está enturmado no ano de origem —, e oferecer um filtro
            que a contradiga devolveria estudante que o passo seguinte recusaria.
          -->
          <div v-if="podeFiltrarEscolaESituacao" class="row g-2 mb-3">
            <div class="col-12 col-md-6">
              <label class="form-label" :for="`${idBase}-escola`">Escola</label>
              <DropDownConsulte
                :id="`${idBase}-escola`"
                v-model="escolaFiltro"
                @change="montarDataSource"
                :array="escolasDisponiveis"
                row-label="nome"
                row-value="id"
                placeholder="Desconsiderar escola (toda a rede)"
                :valor-vazio="0"
              />
            </div>

            <div class="col-12 col-md-6">
              <label class="form-label" :for="`${idBase}-status`">Situação</label>
              <DropDownConsulte
                :id="`${idBase}-status`"
                v-model="statusFiltro"
                :aria-describedby="`${idBase}-status-ajuda`"
                @change="montarDataSource"
                :placeholder="''"
              >
                <option value="Desconsiderar">Desconsiderar</option>
                <option value="Enturmados">Enturmados</option>
                <option value="NaoEnturmados">Não enturmados</option>
                <option value="Matriculados">Matriculados</option>
                <option value="NaoMatriculados">Não matriculados</option>
              </DropDownConsulte>
              <p :id="`${idBase}-status-ajuda`" class="form-text">
                {{ ajudaSituacao }}
              </p>
            </div>
          </div>

          <p :class="erroPesquisa ? 'alert alert-danger' : 'visually-hidden'" role="alert">{{ erroPesquisa }}</p>
          <ConsulteGrid
            :columns="rows"
            :data-source="dataSource"
            :actions="true"
            :hideHeader="true"
            :onlySearchHeader="true"
          >
            <template #header></template>
            <template #botaoSelecionar="context">
              <button type="button"
                :title="`Selecionar ${context.row.nome}`"
                @click="selecionarRegistro(context.row)"
                class="btn btn-link p-0 border-0 align-baseline font-18 text-success me-2"
                style="margin-right: 5px"
                :aria-label="`Selecionar ${context.row.nome}`"
              >
                <i class="uil-arrow-right" aria-hidden="true"></i>
              </button>
            </template>
          </ConsulteGrid>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import { mensagemErroAtendimento } from '@/services/atendimentoestudante/erro-atendimento';
  import ConsulteGrid from '@/components/grid/ConsulteGrid.vue';
import { ConsulteGridColumnsModel } from '@/datasources/consulte-grid';
import FiltroEstudanteDataSource from '@/datasources/filtros/estudante/index.datasource';
import { FiltroEstudanteModel } from '@/models/filtros/filtro-estudante.model';
import DadosLocais from '@/services/helper/storage';
import { useStore } from '@/store';
import { ModalActionTypes } from '@/store/modal/action-types';
import {
  abrirDialogo,
  fecharDialogo,
  novoIdTitulo,
  prenderFoco,
} from '@/uteis/dialogo-acessivel';
import { defineComponent } from 'vue';

  export default defineComponent({
    components: { ConsulteGrid },
    name: 'ModalFiltroEstudante',
    props: {
      hideFooter: Boolean,
      /** Usa a autorização própria do módulo de atendimento, mantendo a mesma busca. */
      contextoAtendimento: { type: Boolean, default: false },
      // quando true, o modal apenas emite o item selecionado via evento 'select' e não altera estado global
      emitOnly: { type: Boolean, default: false },
      // permite filtrar estudantes por escola (enviado por quem abrir o modal)
      escolaId: { type: [Number, String], default: null },
      /**
       * Restringe a pesquisa a quem esta ENTURMADO e MATRICULADO no `ano`. Desligado, o modal
       * segue devolvendo todo aluno cadastrado na escola — o comportamento das telas que ja o usam.
       */
      somenteEnturmadosMatriculados: { type: Boolean, default: false },
      /** Ano da enturmacao e da matricula exigidas. Vazio usa o ano do contexto. */
      ano: { type: [Number, String], default: null },
      /** Turma da enturmacao. Zero ou vazio aceita qualquer turma da escola. */
      codigoTurma: { type: [Number, String], default: null },
      /** Regular, AEE, Progressao... Vazio aceita qualquer tipo. */
      tipoEnturmacao: { type: [Number, String], default: null },
      /**
       * Restringe a quem TEM matricula na escola (no `ano`, quando informado), independentemente de
       * estar ativo ou enturmado — inclui o aluno transferido. Usado na reemissao de boletim/ficha,
       * para nao oferecer aluno que resultaria em "sem matricula" no passo seguinte.
       */
      somenteMatriculadosNaEscola: { type: Boolean, default: false },
      /**
       * Recorte de SITUAÇÃO na escola, no `ano`, das declarações da secretaria:
       * 'Transferidos' (Transferência), 'Efetivos' (Escolaridade) ou 'Aprovados' (Conclusão).
       * Vazio = sem recorte — o padrão de todas as outras telas.
       */
      recorteSituacaoNaEscola: { type: String, default: '' },
    },
    data() {
      return {
        // Id do titulo, alvo do `aria-labelledby` do dialogo (era um id fixo,
        // repetido e inexistente). Ver src/uteis/dialogo-acessivel.ts.
        idTitulo: novoIdTitulo('modal-filtro-estudante'),
        /** Quem tinha o foco antes de abrir, para devolve-lo ao fechar. */
        elementoAnterior: null as HTMLElement | null,
        refreshCounter: 0,
        titulo: '',
        erroPesquisa: '',
        dataSource: new FiltroEstudanteDataSource(0, 5, 0, 0, []),
        /** Ids únicos por instância: o modal pode existir mais de uma vez na mesma página. */
        idBase: `filtro-estudante-${Math.random().toString(36).slice(2, 8)}`,
        /** Escola escolhida no combo. Zero = toda a rede. */
        escolaFiltro: 0,
        /**
         * Recorte de situação. Viaja como NOME para a API (JsonStringEnumConverter).
         * Abre sempre em "Matriculados" (junto com a escola da barra): é quem a pessoa procura no
         * dia a dia. Os outros recortes continuam a uma troca de combo.
         */
        statusFiltro: 'Matriculados',
        /** Escolas que o usuário enxerga, vindas do token — sem ida ao servidor. */
        escolasDisponiveis: [] as { id: number; nome: string }[],
        rows: [
          {
            text: 'Selecionar',
            align: 'center',
            cellsalign: 'center',
            datafield: 'botaoSelecionar',
            editable: false,
            filtertype: 'text',
            filterable: false,
          },
          {
            text: 'Matrícula',
            align: 'center',
            cellsalign: 'center',
            datafield: 'matricula',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'Nome',
            align: 'center',
            cellsalign: 'center',
            datafield: 'nome',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'CPF',
            align: 'center',
            cellsalign: 'center',
            datafield: 'cpf',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'Turma',
            align: 'center',
            cellsalign: 'center',
            datafield: 'nomeTurma',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
          {
            text: 'Série',
            align: 'center',
            cellsalign: 'center',
            datafield: 'nomeSerie',
            editable: false,
            filtertype: 'text',
            filterable: true,
          },
        ] as ConsulteGridColumnsModel[],
      };
    },
    /**
     * Rede de seguranca: ha fluxos que removem o modal sem passar pelo close()
     * (troca de rota com o modal aberto, por exemplo). Sem isto a rolagem do
     * fundo ficaria travada e o foco perdido no <body>.
     */
    unmounted() {
      fecharDialogo(this.elementoAnterior);
    },
    methods: {
      /** Prende o Tab dentro do dialogo. Ver src/uteis/dialogo-acessivel.ts. */
      manterFocoDentro(evento: KeyboardEvent) {
        prenderFoco(evento, this.$refs.dialogRef as HTMLElement | undefined);
      },
      /** Refaz o datasource: o recorte da pesquisa muda a consulta, nao so a pagina. */
      montarDataSource() {
        this.erroPesquisa = '';
        const store = useStore();

        /*
         * A escola do COMBO manda quando a tela deixou o recorte em aberto; a prop `escolaId`
         * continua valendo para as telas que já o impõem. Zero no combo significa "toda a rede" —
         * e aí a escola da rota deixa de recortar (ver `desconsiderarEscola`).
         */
        const desconsiderarEscola = this.podeFiltrarEscolaESituacao && this.escolaFiltro === 0;

        const escola = this.podeFiltrarEscolaESituacao && this.escolaFiltro > 0
          ? this.escolaFiltro
          : Number(this.escolaId ?? store.getters.getCodigoEscola);

        const ano = this.ano === null || this.ano === '' ? null : Number(this.ano);

        this.dataSource = new FiltroEstudanteDataSource(0, 5, 0, 0, [], undefined, escola, {
          contextoAtendimento: this.contextoAtendimento,
          somenteEnturmadosMatriculados: this.somenteEnturmadosMatriculados,
          // Sem ano informado pela tela, vale o da barra: o recorte de situação existe POR ANO, e
          // sem ele o servidor ignora o filtro.
          ano: ano ?? Number(store.getters.getAno) ?? null,
          codigoTurma: this.codigoTurma === null ? null : Number(this.codigoTurma),
          tipoEnturmacao:
            this.tipoEnturmacao === null || this.tipoEnturmacao === ''
              ? null
              : Number(this.tipoEnturmacao),
          somenteMatriculadosNaEscola: this.somenteMatriculadosNaEscola,
          // 'Transferidos' | 'Efetivos' | 'Aprovados' | '' (ver RecorteSituacaoNaEscola no serviço).
          recorteSituacaoNaEscola: this.recorteSituacaoNaEscola as any,
          desconsiderarEscola,
          status: this.podeFiltrarEscolaESituacao ? this.statusFiltro : null,
        }, this.contextoAtendimento ? (erro: unknown) => {
          this.erroPesquisa = mensagemErroAtendimento(erro, 'Não foi possível consultar os estudantes.');
        } : undefined);
      },

      /**
       * As escolas que o usuário enxerga, lidas dos GRUPOS do token — o mesmo conjunto que a barra
       * superior oferece. Sem ida ao servidor: o dado já veio no login, e uma consulta a cada
       * abertura do modal seria uma requisição por pesquisa.
       */
      carregarEscolasDisponiveis() {
        const grupos = DadosLocais.getInstance().getUserData()?.grupos ?? [];
        const porId = new Map<number, string>();

        for (const grupo of grupos) {
          for (const escola of grupo?.escolas ?? []) {
            if (escola?.id > 0 && !porId.has(escola.id)) porId.set(escola.id, escola.nome ?? '');
          }
        }

        this.escolasDisponiveis = [...porId.entries()]
          .map(([id, nome]) => ({ id, nome }))
          .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

        // Abre na escola da barra, que é o que a pessoa espera encontrar — "toda a rede" é uma
        // escolha, não o padrão.
        const daBarra = Number(useStore().getters.getCodigoEscola ?? 0);

        this.escolaFiltro = this.escolasDisponiveis.some((e) => e.id === daBarra) ? daBarra : 0;
      },

      close() {
        fecharDialogo(this.elementoAnterior);
        this.elementoAnterior = null;
        this.refreshCounter += 1;
        this.$emit('close');
      },
      async selecionarRegistro(row: FiltroEstudanteModel) {
        const id = Number(row.id ?? 0);
        const matricula = row.matricula?.toString() ?? String(id);
        const nome = row.nome?.toString() ?? '';

        if ((this as any).emitOnly) {
          this.$emit('select', {
            id,
            codigo: matricula,
            matricula,
            nome,
            nomeSerie: row.nomeSerie?.toString() ?? '',
            nomeTurma: row.nomeTurma?.toString() ?? '',
          });
          this.$emit('close');
          return;
        }

        useStore().dispatch(
          ModalActionTypes.ACTION_SET_CODIGOESTUDANTE_SELECIONADO_TELA_FILTRO,
          id,
        );
        this.$emit('selecionouEstudante');
        this.$emit('close');
      },
    },
    mounted() {
      // Este modal e montado por v-if quando a store manda exibir: montar e abrir
      // sao o mesmo momento. `abrirDialogo` guarda o foco anterior, trava a rolagem
      // do fundo e leva o foco para o primeiro campo util.
      this.$nextTick(() => {
        this.elementoAnterior = abrirDialogo(this.$refs.dialogRef as HTMLElement | undefined);
      });

      // reaplica titulo e inicializa datasource com a escola fornecida (ou a do store)
      this.titulo = 'Selecione o Estudante';
      this.carregarEscolasDisponiveis();
      this.montarDataSource();
    },
    computed: {
      /**
       * A tela deixou o recorte em aberto, então a pesquisa pode oferecer escola e situação.
       *
       * Quando a tela IMPÔS um universo (`somenteEnturmadosMatriculados` na rematrícula,
       * `somenteMatriculadosNaEscola` na reemissão de boletim), os combos ficam de fora: ali o
       * recorte é uma decisão da tela, e um filtro que a contradiga devolveria estudante que o
       * passo seguinte recusaria — com a pessoa achando que o sistema se perdeu.
       */
      podeFiltrarEscolaESituacao(): boolean {
        return (
          !this.somenteEnturmadosMatriculados &&
          !this.somenteMatriculadosNaEscola &&
          !this.recorteSituacaoNaEscola
        );
      },

      ajudaSituacao(): string {
        const ondeEscola = this.escolaFiltro > 0 ? 'nesta escola' : 'na rede';

        switch (this.statusFiltro) {
          case 'Enturmados':
            return `Com enturmação ativa ${ondeEscola} no ano.`;
          case 'NaoEnturmados':
            return `Sem enturmação ativa ${ondeEscola} no ano.`;
          case 'Matriculados':
            return `Com matrícula ${ondeEscola} no ano.`;
          case 'NaoMatriculados':
            return `Sem matrícula ${ondeEscola} no ano.`;
          default:
            return 'Todos os estudantes do recorte de escola escolhido.';
        }
      },
    },
    watch: {
      escolaId() {
        this.montarDataSource();
      },
      codigoTurma() {
        this.montarDataSource();
      },
      ano() {
        this.montarDataSource();
      },
      tipoEnturmacao() {
        this.montarDataSource();
      },
      somenteMatriculadosNaEscola() {
        this.montarDataSource();
      },
      recorteSituacaoNaEscola() {
        this.montarDataSource();
      },
    },
  });
</script>

<style scoped>
  @media (max-width: 576px) {
    .modal-dialog {
      max-width: 100% !important;
      margin: 0;
    }

    .modal-content {
      height: 100vh;
    }
  }

  /* Para telas maiores que 576px */
  @media (min-width: 577px) {
    .modal-dialog {
      max-width: 80% !important;
      margin: auto;
    }
  }

  .card-header {
    padding: 0px !important;
  }

  .modal-consulte-bg {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1050;
    width: 100%;
    height: 100%;
    background-color: #24053d90;
  }
</style>
